import React, { FC, useContext, useMemo, useState } from 'react';

import { RenderElementProps } from 'slate-react';

// components
import AampeItalicText from '../../../../../components/shared/AampeText/AampeItalicText';
import AampeTooltip from '../../../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../../constants/aampeTheme';
// constants
import { FORMULA_IMAGE_SPEC_DATA } from '../../../../../constants/formula';
import { FormulaType } from '../../../../../constants/formulaType';
import ICONS from '../../../../../constants/icons';

// contexts
import { EditorContext } from '../../../../../context/EditorContext';

// helper
import { getEmailFieldData } from '../../../../../helpers/formulaHelpers';
import { createShouldForwardProp } from 'helpers/shouldForwardProp';

// hooks
import { useLockFormulaWarning } from '../../../../../hooks/formulas/useLockFormulaWarning';
import usePushIntegrationsQuery from '../../../../../hooks/pushIntegrations/usePushIntegrationsQuery';
import useOutsideClickDetector from '../../../../../hooks/useOutsideClickDetector';

// models
import {
    ElementKind,
    SectionType,
    SlateElementsData,
    getSlateInputType,
} from '../../../../../models/editor.model';
import {
    EmailFieldDataModel,
    FormulaTypesData,
    ImageSpecsModel,
    MaxCharacterModel,
} from '../../../../../models/formula.model';
import { PushIntegrationModel } from '../../../../../models/pushProvider.model';
import { SurfaceButtonFieldLabel } from '../../../../../models/surface.model';

import FormulaImageSpecsHoverCard from '../../../../../views/formulaCreate/FormulaImageSpecsHoverCard';

import SlateInputCharCount from './SlateInputCharCount';
import SlateInputMenu from './SlateInputMenu';

const shouldForwardProps = createShouldForwardProp([
    'isFocused',
    'readOnly',
    'isFocused',
    'hasWarning',
    'isDisabled',
    'isMultiLine',
    'placeholder',
]);
interface SlateInputElementProps extends RenderElementProps {
    insertDataset: () => void;
    insertCMSPlaceholder: () => void;
    insertUserAttributePlaceholder: () => void;
    insertEmoji: (emoji: string) => void;
    isDisabled: boolean;
    className?: string;
    readOnly?: boolean;
    selectedElement?: { [key: string]: any };
    maxCharacterCounts?: MaxCharacterModel;
    label?: string;
}

interface InputProps {
    isDisabled: boolean;
    inputType: SectionType | ElementKind;
    isEmail?: boolean;
    isMultiLine?: boolean;
    placeholder?: string;
}

interface InputWrapperStyleProps {
    readOnly: boolean;
    isFocused: boolean;
    hasWarning: boolean;
}

const SlateInputElement: FC<React.PropsWithChildren<SlateInputElementProps>> =
    ({
        children,
        attributes,
        insertDataset,
        insertCMSPlaceholder,
        insertEmoji,
        isDisabled,
        element,
        readOnly,
        selectedElement,
        maxCharacterCounts,
        label,
        insertUserAttributePlaceholder,
    }) => {
        const {
            editorDetails: { formulaType },
        } = useContext(EditorContext);

        const [isFocused, setIsFocused] = useState(false);
        const inputRef = useOutsideClickDetector(() => setIsFocused(false));
        const pushIntegrationData = usePushIntegrationsQuery()?.data;

        const providerData = useMemo<PushIntegrationModel | undefined>(
            () =>
                pushIntegrationData?.find((item) =>
                    item.channels?.includes(formulaType!),
                ),
            [formulaType, pushIntegrationData],
        );

        const { hasWarning } = useLockFormulaWarning();

        const setFocus = () => {
            if (!readOnly) {
                setIsFocused(true);
            }
        };

        const isEmail = useMemo<boolean>(
            () => formulaType === FormulaType.email,
            [formulaType],
        );

        const inputType = getSlateInputType(element);

        const placeholder = useMemo(() => {
            return inputType === ElementKind.image
                ? 'Add image URL'
                : (element as any).surfaceFieldName ===
                  SurfaceButtonFieldLabel.Text
                ? 'Add button text'
                : (element as any).surfaceFieldName ===
                  SurfaceButtonFieldLabel.Action
                ? 'Add button action '
                : '';
        }, [element, inputType]);

        const charCountMessage = useMemo<string>(() => {
            let charCount = 0;
            switch (inputType) {
                case SectionType.body:
                    charCount = maxCharacterCounts?.body ?? 0;
                    break;

                case SectionType.header:
                    charCount = maxCharacterCounts?.header ?? 0;
                    break;

                case SectionType.message:
                    charCount = maxCharacterCounts?.message ?? 0;
                    break;

                default:
                    break;
            }
            return charCount
                ? `Longest ${inputType} length: ${charCount} character${
                      charCount > 1 ? 's' : ''
                  }`
                : '';
        }, [inputType, maxCharacterCounts]);

        const disableSlateInputMenu = useMemo<boolean>(() => {
            if (!isFocused) {
                return true;
            }
            return (
                SlateElementsData[selectedElement?.kind as ElementKind]
                    ?.disableLowerMenu || false
            );
        }, [isFocused, selectedElement]);

        const specs_data = useMemo<ImageSpecsModel | undefined>(() => {
            if (
                inputType === ElementKind.image &&
                providerData?.push_provider_name
            ) {
                return FORMULA_IMAGE_SPEC_DATA[providerData.push_provider_name];
            }
        }, [inputType, providerData]);

        const warningCount = useMemo<number | undefined>(
            () =>
                FormulaTypesData[formulaType as FormulaType]
                    ?.charWarningLimits?.[inputType as SectionType],
            [formulaType, inputType],
        );

        const showWarning = useMemo<boolean>(
            () =>
                !!(warningCount && maxCharacterCounts?.header! > warningCount),
            [maxCharacterCounts, warningCount],
        );
        const emailFieldData = useMemo<EmailFieldDataModel>(
            () => getEmailFieldData(inputType),
            [inputType],
        );

        const slateFieldLabel = useMemo<string>(() => {
            if (isEmail) {
                return emailFieldData.label;
            }
            if (
                inputType === SectionType.buttonText &&
                formulaType === FormulaType.contentCards
            ) {
                return inputType.replace('_', ' ');
            }
            return SlateElementsData[inputType]?.label || inputType;
        }, [emailFieldData, isEmail, inputType, formulaType]);

        const isMultiLine = useMemo<boolean>(() => {
            if (isEmail) {
                return emailFieldData.isMultiLine;
            }
            return !!SlateElementsData[inputType]?.isMultiLine;
        }, [emailFieldData, isEmail, inputType]);

        const isRequired = useMemo<boolean>(() => {
            if (isEmail) {
                return emailFieldData.isRequired;
            }
            return !!SlateElementsData[inputType]?.isRequired;
        }, [emailFieldData, isEmail, inputType]);

        const isHideEmoji = useMemo<boolean>(() => {
            if (isEmail) {
                return emailFieldData.hideEmoji;
            }
            return !!SlateElementsData[inputType]?.hideEmoji;
        }, [emailFieldData, inputType, isEmail]);

        const isHideCmsPlaceholder = useMemo<boolean>(() => {
            if (isEmail) {
                return emailFieldData.hideCmsPlaceholder;
            }
            return !!SlateElementsData[inputType]?.hideCMSPlaceholder;
        }, [emailFieldData, inputType, isEmail]);

        const hasContent = useMemo<boolean>(() => {
            const checkContentInNode = (nodes: any[]): boolean => {
                return nodes.some((node: any) => {
                    // if there's any normal text node.
                    if (node?.text?.length > 0) {
                        return true;
                    }
                    // if there's any component/dataset/placeholder node.
                    if (node.id) {
                        return true;
                    }

                    if (node?.props?.parent?.children.length) {
                        return checkContentInNode(node.props.parent.children);
                    }

                    return false;
                });
            };
            return checkContentInNode(children);
        }, [children]);

        return (
            <SlateInputWrapper {...attributes}>
                <SlateLabel contentEditable={false}>
                    <LabelText>
                        <span>{label ? label : slateFieldLabel}</span>
                        {isRequired && <AampeItalicText text={'(Required)'} />}

                        {inputType === ElementKind.image && specs_data && (
                            <AampeTooltip
                                title={
                                    <FormulaImageSpecsHoverCard
                                        specs_data={specs_data}
                                    />
                                }
                                placement="right"
                                arrow
                                arrowColor={aampeTheme.colors.white}
                                customZIndex={4000}
                                backgroundColor={aampeTheme.colors.white}
                                color={aampeTheme.colors.black}
                                maxWidth={400}
                                padding={0}
                            >
                                <IconContainer>
                                    <Info size={16} />
                                </IconContainer>
                            </AampeTooltip>
                        )}
                    </LabelText>
                    {!SlateElementsData[inputType]?.hideCharCount && (
                        <SlateInputCharCount
                            showWarning={showWarning}
                            warningCount={warningCount}
                            charCountMessage={charCountMessage}
                        />
                    )}
                </SlateLabel>
                <SlateInputBox
                    ref={inputRef}
                    readOnly={!!readOnly}
                    isFocused={isFocused}
                    hasWarning={hasWarning}
                    data-name={slateFieldLabel}
                >
                    <SlateInput
                        onClick={setFocus}
                        inputType={inputType}
                        isMultiLine={isMultiLine}
                        isDisabled={isDisabled}
                        isEmail={isEmail}
                        placeholder={
                            !isFocused && !hasContent ? placeholder : ''
                        }
                    >
                        {children}
                    </SlateInput>
                    <div contentEditable={false} style={{ userSelect: 'none' }}>
                        {/* Regular div used to circumvent issue with contentEditable propagation with styled components */}
                        {inputType !== ElementKind.image && (
                            <SectionLowerMenu contentEditable={false}>
                                {!readOnly ? (
                                    <SlateInputMenu
                                        insertDataset={insertDataset}
                                        insertEmoji={
                                            isHideEmoji ? null : insertEmoji
                                        }
                                        insertCMSPlaceholder={
                                            isHideCmsPlaceholder
                                                ? null
                                                : insertCMSPlaceholder
                                        }
                                        insertUserAttributePlaceholder={
                                            insertUserAttributePlaceholder
                                        }
                                        isDisabled={disableSlateInputMenu}
                                    />
                                ) : (
                                    <div />
                                )}
                            </SectionLowerMenu>
                        )}
                    </div>
                </SlateInputBox>
            </SlateInputWrapper>
        );
    };

const SlateInputWrapper = styled.div({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
});

const SlateLabel = styled.div({
    textTransform: 'capitalize',
    marginBottom: aampeTheme.spacing(1),
    userSelect: 'none',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
});
//description
const SlateInputBox = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<InputWrapperStyleProps>((props) => ({
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid  ${
        props.hasWarning
            ? aampeTheme.colors.error
            : props.isFocused
            ? aampeTheme.colors.solidA02
            : aampeTheme.colors.bordersWG01
    }`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    background: aampeTheme.colors.white,
}));

const SlateInput = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<InputProps>(({ isDisabled, isMultiLine, placeholder }) => ({
    color: isDisabled
        ? aampeTheme.colors.bordersWG01
        : aampeTheme.colors.gray700,
    outline: 'none',
    fontSize: aampeTheme.size.font.default,
    lineHeight: '30px',
    padding: aampeTheme.spacing4(2),
    wordBreak: 'break-word',
    minHeight: isMultiLine ? '200px' : '50px',
    '&::before': {
        content: `"${placeholder}"`,
        color: aampeTheme.colors.solidWG02,
        display: 'inline-block',
    },
}));

const SectionLowerMenu = styled.div({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'flex-end',
    justifyContent: 'end',
    padding: aampeTheme.spacing(0, 0.5, 0.5, 0),
    userSelect: 'none',
});

const Info = styled(ICONS.info)(() => ({
    cursor: 'pointer',
    color: aampeTheme.colors.gray600,
}));

const LabelText = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
    fontSize: aampeTheme.size.font.default,
    fontWeight: 500,
    lineHeight: '20px',
    color: aampeTheme.colors.gray800,
    textTransform: 'capitalize',
});

const IconContainer = styled.div({
    lineHeight: '0px',
});
export default SlateInputElement;
